import React from 'react';
import { ArrowRight, Globe, TrendingUp, Cpu, MousePointerClick } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  id: "01",
  title: "OTA Distribution & Rate Parity Audits",
  description: "A complete audit of your online channels to uncover hidden undercutting, high commission fees, and missed ranking opportunities across every major travel portal.",
  outcome: <>
        Clean rate parity across all extranets, stopping OTAs from undercutting your direct rate, and eliminating{" "}
        <Link href="https://hotelrevbaba.com/case-study-how-sunil-singh-increased-a-midscale-hotels-ota-revenue-by-22/" className="underline text-secondary-foreground hover:text-foreground font-semibold transition-colors not-italic">
          commission leakage
        </Link>
        {" "}following our{" "}
        <Link href="https://hotelrevbaba.com/how-to-conduct-a-hotel-revenue-audit-complete-guide/" className="underline text-secondary-foreground hover:text-foreground font-semibold transition-colors not-italic">
          hotel revenue audit checklist
        </Link>
        .
      </>,
  icon: Globe,
  href: "/ServicesPage",
  ariaLabel: "OTA distribution and rate parity audits"
}, {
  id: "02",
  title: "Dynamic Pricing & Yield Management",
  description: "Daily rate updates based on your pickup pace, local compset movements, city events, and seasonal demand swings to lift your RevPAR and ADR.",
  outcome: <>
        Active pricing that captures higher rates during peak weekends and defends base occupancy when demand softens, driving disciplined{" "}
        <Link href="https://hotelrevbaba.com/how-to-increase-hotel-adr-5-datadriven-strategies/" className="underline text-secondary-foreground hover:text-foreground font-semibold transition-colors not-italic">
          ADR optimization
        </Link>
        .
      </>,
  icon: TrendingUp,
  href: "/ServicesPage",
  ariaLabel: "Dynamic pricing and yield management"
}, {
  id: "03",
  title: "Channel Manager Integration & Inventory Mapping",
  description: "Two-way inventory syncing between your PMS and channel manager. I map room categories, rate plans, and minimum stay rules to prevent overbooking and rate lag.",
  outcome: <>
        Instant room availability sync across every extranet with clean room category mapping and reliable rate updates built on proven{" "}
        <Link href="https://hotelrevbaba.com/channel-management-best-practices-for-2025/" className="underline text-secondary-foreground hover:text-foreground font-semibold transition-colors not-italic">
          channel management best practices
        </Link>
        .
      </>,
  icon: Cpu,
  href: "/ServicesPage",
  ariaLabel: "Channel manager integration and inventory mapping"
}, {
  id: "04",
  title: "Direct Booking & Website Conversion Strategy",
  description: "Refining your direct booking engine, setting up Google Hotel Ads, and creating exclusive direct-booking perks so guests book with you instead of high-commission OTAs.",
  outcome: "Higher take-home profit through increased direct website reservations and lower overall distribution costs.",
  icon: MousePointerClick,
  href: "/ServicesPage",
  ariaLabel: "Direct booking and website conversion strategy"
}];
export default function ServiceGrid() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="12489" id="threepillarsservicegrid" className="bg-white py-24 md:py-32 text-foreground border-t border-border relative overflow-hidden">
      {/* Subtle ambient lighting */}
      <div className="hidden sm:block absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[350px] bg-[hsl(43,46%,55%)]/5 blur-[120px] pointer-events-none rounded-lg" />

      <div className="relative mx-auto w-full max-w-7xl px-6 md:px-8">
        {/* Header Block */}
        <div ref={header.ref} className="max-w-3xl mx-auto text-center">
          <div className={`transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
            <p className="text-secondary font-mono tracking-[0.2em] uppercase text-xs md:text-sm font-semibold mb-3">
              Services
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-tight">
              Core Revenue &amp; Distribution Services
            </h2>
            <p className="mt-4 text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              Hands-on distribution management and daily pricing updates built for independent hotel owners and general managers.
            </p>
          </div>
        </div>

        {/* 4-Tile Modular Grid (1-Col Mobile, 2x2 Desktop) */}
        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {services.map((service, index) => <ServiceTile key={service.title} data-index={index} index={index} id={service.id} title={service.title} description={service.description} outcome={service.outcome} icon={service.icon} href={service.href} ariaLabel={service.ariaLabel} />)}
        </div>
      </div>
    </section>;
}
type ServiceTileProps = {
  index: number;
  id: string;
  title: string;
  description: string;
  outcome: React.ReactNode;
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  href: string;
  ariaLabel: string;
  "data-index"?: number;
};
function ServiceTile({
  index,
  id,
  title,
  description,
  outcome,
  icon: IconComponent,
  href,
  ariaLabel,
  ...rest
}: ServiceTileProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const delay = index % 2 * 120;
  return <div ref={ref} className={`group relative flex flex-col justify-between p-7 lg:p-8 rounded-xl bg-white border border-border transition-all duration-300 hover:border-secondary/70 hover:bg-secondary/5 hover:shadow-[0_0_30px_-5px_hsla(43,46%,55%,0.18)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
    transitionDelay: `${delay}ms`
  }} {...rest}>
      <div>
        {/* Card Header: Icon + Number Index */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center justify-center size-12 rounded-xl bg-secondary/10 border border-secondary/20 text-secondary group-hover:bg-secondary group-hover:text-[hsl(222,47%,7%)] transition-colors duration-300">
            <IconComponent className="size-5" strokeWidth={1.75} />
          </div>
          <span className="font-mono text-xs text-muted-foreground group-hover:text-secondary/80 transition-colors font-medium tracking-wider">
            {id}
          </span>
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-foreground tracking-tight leading-snug group-hover:text-secondary transition-colors duration-200">
          {title}
        </h3>

        {/* Description */}
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
          {description}
        </p>

        {/* Gold-Bordered Blockquote Outcome Highlight */}
        <blockquote className="mt-5 border-l-2 border-secondary bg-secondary/10 px-3.5 py-2.5 rounded-sm text-xs md:text-[13px] font-sans font-medium italic text-foreground not-italic-reset">
          <span className="text-secondary font-semibold not-italic mr-1.5">Outcome:</span>
          {outcome}
        </blockquote>
      </div>

      {/* Footer Link */}
      <div className="mt-8 pt-4 border-t border-border flex items-center justify-between">
        <Link to={href} aria-label={ariaLabel} className="inline-flex items-center gap-2 text-xs font-semibold text-secondary tracking-wide uppercase hover:text-foreground transition-colors duration-200">
          <span>Explore service</span>
          <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
      </div>
    </div>;
}